<template>
    <transition>
        <section
            :class="[classes, customClass]"
            :style="styles"
            role="tooltip"
            tabindex="-1"
        >
            <slot></slot>
        </section>
    </transition>
</template>
<script>
import utils from '../../utils';
import mixins from '../mixins';
export default {
    name: 'markets-ui-popover',
    mixins: [mixins],
    props: {
        target: {
            required: true
        },
        width: {
            type: Number,
            default: 200
        },
        arrowHeight: {
            type: Number,
            default: 8
        },
        customClass: {
            type: String,
            default: ''
        },
        allowPositions: {
            type: Array,
            default: () => {
                return ['left', 'right', 'bottom', 'top'];
            }
        },
        priority: {
            type: Array,
            default: () => {
                return ['bottom', 'right', 'top', 'left'];
            }
        },
        positionPriority: {
            type: Object,
            default: () => {
                return {
                    bottom: ['center', 'right', 'left'],
                    right: ['center', 'bottom', 'top'],
                    top: ['center', 'right', 'left'],
                    left: ['center', 'bottom', 'top']
                };
            }
        },
        directionPriority: {
            type: Object,
            default: () => {
                return {
                    bottom: ['left', 'center', 'right'],
                    right: ['top', 'center', 'bottom'],
                    top: ['left', 'center', 'right'],
                    left: ['top', 'center', 'bottom']
                };
            }
        },
        visible: {
            type: Boolean,
            default: false
        },
        skin: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            positionInfo: null,
            targetRect: {}
        };
    },
    created() {
        this.namespace = utils.namespace('ui-popover', false);
        this.containerRect = {};
        this.targetRect = {};
        this.rect = {};
        this.ismoving = false;
    },
    beforeMount() {
        this.destroyElement();
    },
    mounted() {
        this.update();
    },
    beforeDestroy() {
        this.removeEvents();
        this.destroyElement();
    },
    computed: {
        classes() {
            const cls = [
                this.mdsNS('popover'),
                `${this.mdsNS('popover--width')}-${this.width}px`,
                `${this.namespace()}`
            ];
            if (this.visible) {
                cls.push(this.namespace('__visible'));
            }
            if (this.positionInfo) {
                cls.push(
                    `${this.mdsNS('popover')}--${this.positionInfo.type}-${
                        this.positionInfo.direction
                    }`
                );
            }
            if (this.skin) {
                cls.push(
                    `${this.namespace()}__${utils.getSkinSuffix(this.skin)}`
                );
            }
            return cls;
        },
        styles() {
            if (this.positionInfo) {
                return {
                    top: `${this.positionInfo.top}px`,
                    left: `${this.positionInfo.left}px`,
                    width: `${this.width}px`
                };
            }
            return {
                top: '-1000px',
                left: '-1000px',
                width: `${this.width}px`
            };
        }
    },
    watch: {
        visible() {
            this.update();
        },
        width() {
            this.update();
        },
        target: {
            handler: function() {
                this.update();
            },
            deep: true
        }
    },
    methods: {
        destroyElement() {
            if (this.$el && this.$el.parentNode) {
                this.$el.parentNode.removeChild(this.$el);
                this.positionInfo = null;
            }
        },
        bindEvents() {
            document.addEventListener('click', this.validateClick);
            document.addEventListener('mousedown', this.mouseDown);
            document.addEventListener('mouseup', this.mouseUp);
            this.$el.addEventListener('click', this.click);
            this.$el.addEventListener('keydown', this.keyDown);
        },
        removeEvents() {
            document.removeEventListener('click', this.validateClick);
        },
        click(e) {
            if (e && e.stopPropagation) {
                e.stopPropagation();
            }
        },
        mouseDown(e) {
            this._movex = e.pageX;
            this._movey = e.pageY;
            this.ismoving = true;
        },
        mouseUp(e) {
            if (
                Math.abs(e.pageX - this._movex) < 2 &&
                Math.abs(e.pageY - this._movey) < 2
            ) {
                this.ismoving = false;
            }
        },
        keyDown(e) {
            const keyCode = e.keyCode;
            if (
                utils.keyCode.isESC(keyCode) ||
                utils.keyCode.isSpace(keyCode)
            ) {
                this.$emit('hide');
                e.stopPropagation();
                e.preventDefault();
            }
        },
        validateClick(e) {
            const elem = e.target || e.srcElement;
            if (this.$el.contains(elem) || this.$el === elem || this.ismoving) {
                return;
            }
            this.destroyElement();
            this.$emit('hide');
        },
        update() {
            clearTimeout(this.timer);
            this.removeEvents();
            if (!this.visible) {
                this.destroyElement();
                return;
            }
            this.timer = setTimeout(() => {
                this.bindEvents();
            }, 100);

            if (!this.target) {
                return;
            }
            if (
                this.target instanceof HTMLElement ||
                this.target instanceof SVGElement
            ) {
                this.targetRect = utils.extend(
                    utils.outerSizes(this.target, true)
                );
            } else {
                this.targetRect = utils.extend({}, this.target);
            }
            document.body.appendChild(this.$el);

            this.$nextTick(function() {
                this.containerRect = {
                    width: document.body.offsetWidth,
                    height: document.body.offsetHeight,
                    left: 0,
                    top: 0
                };

                // set to null, then compute the class and remove the style
                this.positionInfo = null;
                // need to wait the dom rerender to re-calcaulate the popover width and height without arrow
                this.$nextTick(function() {
                    this.rect = utils.outerSizes(this.$el, true);
                    this.positionInfo = this.getPositionInfo();
                    this.$nextTick(() => {
                        this.$el.focus(); // We need to change the focus until popover adjust its position in dom
                    });
                });
            });
        },
        getPositionInfo() {
            const positionsList = this.allowPositions.map(position => {
                const { targetRect, containerRect } = this;
                const info = {
                    type: position,
                    targetRect,
                    containerRect,
                    width: this.rect.width,
                    height: this.rect.height,
                    top: 0,
                    left: 0,
                    blank: 0
                };

                this[`${position}Handler`](info);
                info.inner =
                    info.left >= info.containerRect.left &&
                    info.top >= info.containerRect.top &&
                    info.left + info.width <=
                        info.containerRect.left + info.containerRect.width &&
                    info.top + info.height <=
                        info.containerRect.top + info.containerRect.height;
                return info;
            });
            const prePosition = this.positionInfo ? this.positionInfo.type : '';
            return this.getBestByPosition(
                positionsList,
                this.priority,
                prePosition
            );
        },
        leftHandler(info) {
            info.width += this.arrowHeight;
            info.left = info.targetRect.left - info.width;
            info.blank = info.left - info.containerRect.left;
            this.getVerticalArrowPosition(info);
        },
        rightHandler(info) {
            info.width += this.arrowHeight;
            info.left = info.targetRect.left + info.targetRect.width;
            info.blank =
                info.containerRect.left +
                info.containerRect.width -
                (info.left + info.width);
            this.getVerticalArrowPosition(info);
        },
        bottomHandler(info) {
            info.height += this.arrowHeight;
            info.top = info.targetRect.top + info.targetRect.height;
            info.blank =
                info.containerRect.top +
                info.containerRect.height -
                (info.top + info.height);
            this.getHorizontalArrowPosition(info);
        },
        topHandler(info) {
            info.height += this.arrowHeight;
            info.top = info.targetRect.top - info.height;
            info.blank = info.top - info.containerRect.top;
            this.getHorizontalArrowPosition(info);
        },
        getVerticalArrowPosition(info) {
            const directions = {
                center: info => {
                    return (
                        info.targetRect.top +
                        info.targetRect.height * 0.5 -
                        info.height * 0.5
                    );
                },
                bottom: info => {
                    return info.targetRect.top + info.targetRect.height * 0.5;
                },
                top: info => {
                    return (
                        info.targetRect.top +
                        info.targetRect.height * 0.5 -
                        info.height
                    );
                }
            };
            const directionList = ['top', 'center', 'bottom'].map(direction => {
                const d = {
                    type: direction,
                    top: directions[direction](info)
                };
                d.blank = d.top - info.containerRect.top;
                d.inner =
                    d.top >= info.containerRect.top &&
                    d.top + info.height <=
                        info.containerRect.top + info.containerRect.height;
                return d;
            });

            const bestDirection = this.getBestDirection(directionList, info);
            info.top = bestDirection.top;
            info.direction = bestDirection.type;
        },
        getHorizontalArrowPosition(info) {
            const positions = {
                center: info => {
                    return (
                        info.targetRect.left +
                        info.targetRect.width * 0.5 -
                        info.width * 0.5
                    );
                },
                right: info => {
                    return info.targetRect.left + info.targetRect.width * 0.5;
                },
                left: info => {
                    return (
                        info.targetRect.left +
                        info.targetRect.width * 0.5 -
                        info.width
                    );
                }
            };
            const positionsList = ['left', 'center', 'right'].map(position => {
                const p = {
                    type: position,
                    left: positions[position](info)
                };
                p.blank = p.left - info.containerRect.left;
                p.inner =
                    p.left >= info.containerRect.left &&
                    p.left + info.width <=
                        info.containerRect.left + info.containerRect.width;
                return p;
            });
            const bestDirection = this.getBestDirection(positionsList, info);
            info.left = bestDirection.left;
            info.direction = bestDirection.type;
        },
        getBestByPosition(
            positions,
            positionPriority,
            prePosition,
            directionPriority
        ) {
            if (positionPriority && positionPriority.length) {
                this.orderByPriority(positions, positionPriority);
            }
            const bestPositions = positions.filter(position => position.inner);
            if (bestPositions.length) {
                return this.getBestPosition(bestPositions, prePosition);
            } else {
                return this.getBestByBlank(positions, directionPriority);
            }
        },
        getBestDirection(directionList, info) {
            const positionPriority = this.positionPriority[info.type];
            const directionPriority = this.directionPriority[info.type];
            const prePosition =
                this.positionInfo && this.positionInfo.type === info.type
                    ? this.positionInfo.direction
                    : '';
            return this.getBestByPosition(
                directionList,
                positionPriority,
                prePosition,
                directionPriority
            );
        },
        orderByPriority: function(list, priority) {
            list.sort((a, b) => {
                const ai = this.getPriorityIndex(priority, a.type);
                const bi = this.getPriorityIndex(priority, b.type);
                return ai - bi;
            });
            return list;
        },
        getPriorityIndex(priority, type) {
            let i;
            for (i = 0; i < priority.length; i++) {
                const item = priority[i];
                if (item === type) {
                    return i;
                }
            }
            return i;
        },
        getBestPosition(bestPositions, type) {
            if (bestPositions.length <= 1 || !type) {
                return bestPositions[0];
            }
            const bestPosition = bestPositions.filter(
                position => position.type === type
            )[0];
            if (bestPosition) {
                return bestPosition;
            } else {
                return bestPositions[0];
            }
        },
        getBestByBlank(positions, priority) {
            if (priority && priority.length) {
                this.orderByPriority(positions, priority);
            }
            const bestDirections = positions.filter(
                position => position.blank >= 0
            );

            if (bestDirections.length) {
                return bestDirections[0];
            } else {
                return this.getBestByDistance(positions);
            }
        },
        getBestByDistance(positions) {
            positions.sort((a, b) => {
                return b.blank - a.blank;
            });
            return positions[0];
        }
    }
};
</script>

<style lang="scss">
$namespace: 'markets-ui-popover';
@import '../../mds/constants';

.#{$namespace} {
    &__visible {
        visibility: visible;
        opacity: 1;
    }
    &:focus {
        outline: none;
    }
    &__header {
        margin-bottom: $mds-space-1-x;
        &-title {
            display: inline-flex;
            align-items: center;
            button {
                margin-right: $mds-space-three-quarter-x;
            }
        }
    }
    //todo: use mixins
    &__dark-gray {
        background: $mds-background-color-dark-gray;
        color: $mds-text-color-primary-on-dark;
        .mds-popover__header,
        .mds-v2-popover__header {
            border-bottom: $mds-border-container-on-all-backgrounds;
        }
        .mds-popover__title,
        .mds-v2-popover__title {
            color: $mds-text-color-primary-on-dark;
        }
    }
    &__dark-gray.mds-popover.mds-popover--right-top:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--right-top:after,
    &__dark-gray.mds-popover.mds-popover--right-center:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--right-center:after,
    &__dark-gray.mds-popover.mds-popover--right-bottom:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--right-bottom:after {
        border-right-color: $mds-background-color-dark-gray;
    }
    &__dark-gray.mds-popover.mds-popover--bottom-right:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--bottom-right:after,
    &__dark-gray.mds-popover.mds-popover--bottom-center:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--bottom-center:after,
    &__dark-gray.mds-popover.mds-popover--bottom-left:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--bottom-left:after {
        border-bottom-color: $mds-background-color-dark-gray;
    }
    &__dark-gray.mds-popover.mds-popover--left-top:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--left-top:after,
    &__dark-gray.mds-popover.mds-popover--left-bottom:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--left-bottom:after,
    &__dark-gray.mds-popover.mds-popover--left-center:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--left-center:after {
        border-left-color: $mds-background-color-dark-gray;
    }
    &__dark-gray.mds-popover.mds-popover--top-center:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--top-center:after,
    &__dark-gray.mds-popover.mds-popover--top-left:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--top-left:after,
    &__dark-gray.mds-popover.mds-popover--top-right:after,
    &__dark-gray.mds-v2-popover.mds-v2-popover--top-right:after {
        border-top-color: $mds-background-color-dark-gray;
    }

    &.mds-popover--bottom-left,
    &.mds-popover--bottom-right,
    &.mds-popover--top-left,
    &.mds-popover--top-right,
    &.mds-popover--left-bottom,
    &.mds-popover--left-top,
    &.mds-popover--right-top,
    &.mds-popover--right-bottom,
    &.mds-popover--bottom-center,
    &.mds-popover--top-center,
    &.mds-popover--left-center,
    &.mds-popover--right-center,
    &.mds-v2-popover--bottom-left,
    &.mds-v2-popover--bottom-right,
    &.mds-v2-popover--top-left,
    &.mds-v2-popover--top-right,
    &.mds-v2-popover--left-bottom,
    &.mds-v2-popover--left-top,
    &.mds-v2-popover--right-top,
    &.mds-v2-popover--right-bottom,
    &.mds-v2-popover--bottom-center,
    &.mds-v2-popover--top-center,
    &.mds-v2-popover--left-center,
    &.mds-v2-popover--right-center {
        right: auto;
        bottom: auto;
    }

    &.mds-popover--top-center,
    &.mds-popover--top-left,
    &.mds-popover--top-right,
    &.mds-v2-popover--top-center,
    &.mds-v2-popover--top-left,
    &.mds-v2-popover--top-right {
        &.#{$namespace}-popover__dark-gray:after {
            border-top-color: $mds-background-color-dark-gray;
        }
    }

    &.mds-popover--left-bottom,
    &.mds-popover--left-center,
    &.mds-popover--left-top,
    &.mds-v2-popover--left-bottom,
    &.mds-v2-popover--left-center,
    &.mds-v2-popover--left-top {
        &.#{$namespace}-popover__dark-gray:after {
            border-left-color: $mds-background-color-dark-gray;
        }
    }

    &.mds-popover--bottom-center,
    &.mds-popover--bottom-left,
    &.mds-popover--bottom-right,
    &.mds-v2-popover--bottom-center,
    &.mds-v2-popover--bottom-left,
    &.mds-v2-popover--bottom-right {
        &.#{$namespace}-popover__dark-gray:after {
            border-bottom-color: $mds-background-color-dark-gray;
        }
    }

    &.mds-popover--right-center,
    &.mds-popover--right-top,
    &.mds-popover--right-bottom,
    &.mds-v2-popover--right-center,
    &.mds-v2-popover--right-top,
    &.mds-v2-popover--right-bottom {
        &.#{$namespace}-popover__dark-gray:after {
            border-right-color: $mds-background-color-dark-gray;
        }
    }

    &.mds-popover--bottom-left,
    &.mds-popover--bottom-right,
    &.mds-popover--top-left,
    &.mds-popover--top-right,
    &.mds-popover--left-bottom,
    &.mds-popover--left-top,
    &.mds-popover--bottom-center,
    &.mds-popover--top-center,
    &.mds-popover--left-center,
    &.mds-v2-popover--bottom-left,
    &.mds-v2-popover--bottom-right,
    &.mds-v2-popover--top-left,
    &.mds-v2-popover--top-right,
    &.mds-v2-popover--left-bottom,
    &.mds-v2-popover--left-top,
    &.mds-v2-popover--bottom-center,
    &.mds-v2-popover--top-center,
    &.mds-v2-popover--left-center {
        margin-left: 0;
    }
}
</style>
